import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/qr";
import { CATEGORIES, SUBCATEGORIES } from "@/lib/categories";

const fld = "w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white";

function Review({ x, reload }) {
  const [f, setF] = useState({ title: x.name, address: x.address || "", postal_code: x.postal_code || "", city: x.city || "", country: x.country || "", phone: "", description: x.note || "", category: "", subcategory: "" });
  const [busy, setBusy] = useState(false);
  const s = (p) => setF((v) => ({ ...v, ...p }));
  const cats = CATEGORIES.filter((c) => !["komunitete", "punekerkues"].includes(c.key));
  const publish = async () => {
    if (!f.category) return toast.error("Zgjidh kategorinë");
    setBusy(true);
    try { await api.post(`/admin/suggestions/${x.id}/publish`, f); toast.success("U dërgua te Pritje. GjejKredit i jepen kur ta aprovoni."); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reject = async () => {
    const note = window.prompt("Arsyeja (opsionale)");
    if (note === null) return;
    try { await api.post(`/admin/suggestions/${x.id}/reject`, { note }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white rounded-2xl p-3 border border-amber-200 space-y-2" data-testid={`sug-${x.id}`}>
      <div className="flex gap-2">
        {x.photo && <img src={x.photo} alt="" className="w-24 h-20 rounded-xl object-cover" />}
        {x.logo && <img src={x.logo} alt="" className="w-12 h-12 rounded-full object-cover border" />}
        <div className="min-w-0 text-[11px] text-slate-500"><b className="text-slate-800" translate="no">#{String(x.member_no ?? "").padStart(2, "0")} {x.user_name}</b><div>{fmtDT(x.created_at)}</div>
          {x.lat && <a href={`https://www.openstreetmap.org/?mlat=${x.lat}&mlon=${x.lng}#map=18/${x.lat}/${x.lng}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-0.5" data-testid={`sug-map-${x.id}`}><MapPin className="w-3 h-3" />Vendndodhja (GPS)</a>}</div>
      </div>
      <input data-testid={`sug-title-${x.id}`} value={f.title} onChange={(e) => s({ title: e.target.value })} className={fld} placeholder="Emri" />
      <div className="grid grid-cols-2 gap-2">
        <select data-testid={`sug-cat-${x.id}`} value={f.category} onChange={(e) => s({ category: e.target.value, subcategory: "" })} className={fld}><option value="">Kategoria *</option>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
        <select data-testid={`sug-sub-${x.id}`} value={f.subcategory} onChange={(e) => s({ subcategory: e.target.value })} className={fld}><option value="">Nënkategoria</option>{(SUBCATEGORIES[f.category] || []).map((v) => <option key={v}>{v}</option>)}</select>
        <input data-testid={`sug-address-${x.id}`} value={f.address} onChange={(e) => s({ address: e.target.value })} className={fld} placeholder="Adresa" />
        <input data-testid={`sug-postal-${x.id}`} value={f.postal_code} onChange={(e) => s({ postal_code: e.target.value })} className={fld} placeholder="Kodi postar" />
        <input data-testid={`sug-city-${x.id}`} value={f.city} onChange={(e) => s({ city: e.target.value })} className={fld} placeholder="Qyteti" />
        <input data-testid={`sug-country-${x.id}`} value={f.country} onChange={(e) => s({ country: e.target.value })} className={fld} placeholder="Shteti" />
        <input data-testid={`sug-phone-${x.id}`} value={f.phone} onChange={(e) => s({ phone: e.target.value })} className={fld} placeholder="Telefoni" />
      </div>
      <textarea data-testid={`sug-desc-${x.id}`} value={f.description} onChange={(e) => s({ description: e.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 p-2 text-sm" placeholder="Përshkrimi" />
      <div className="flex gap-2">
        <button data-testid={`sug-publish-${x.id}`} disabled={busy} onClick={publish} className="flex-1 h-10 rounded-xl bg-emerald-500 text-white text-sm font-bold disabled:opacity-60">Dërgo në Pritje për aprovim</button>
        <button data-testid={`sug-reject-${x.id}`} onClick={reject} className="h-10 px-4 rounded-xl bg-rose-50 text-rose-600 text-sm font-bold">Refuzo</button>
      </div>
    </div>
  );
}

export default function AdminSuggestions() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/suggestions").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return <div className="p-6 text-center text-sm text-slate-500">Duke ngarkuar...</div>;
  const pending = list.filter((x) => x.status === "pending");
  const done = list.filter((x) => x.status !== "pending");
  return (
    <div className="space-y-2" data-testid="admin-suggestions">
      <div className="text-sm font-bold text-slate-900">Biznese të treguara nga përdoruesit ({pending.length} në pritje)</div>
      {pending.map((x) => <Review key={x.id} x={x} reload={load} />)}
      {!pending.length && <p className="text-xs text-slate-400">Asnjë biznes në pritje.</p>}
      {done.map((x) => (
        <div key={x.id} className="text-xs text-slate-600 bg-white rounded-xl p-2 border border-slate-100" data-testid={`sug-done-${x.id}`}>
          <b translate="no">{x.name}</b> · {x.user_name} · {x.status === "published" ? `U publikua (+${x.points || 0} kredite)` : x.status === "review" ? "Në Pritje për aprovim" : "Refuzuar"} · {fmtDT(x.created_at)}
        </div>
      ))}
    </div>
  );
}
